import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TrendingUp, PieChart, Home, AlertCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { XAxis, YAxis, ResponsiveContainer, AreaChart, Area, PieChart as RePieChart, Pie, Cell, Tooltip } from 'recharts';
import { PortfolioCard } from './PortfolioCard';
import { portfolioApi } from '../../services/api';
import { getMyTransactions } from '../../services/adminDealFlowApi';
import { PortfolioPosition } from '../../types';
import { useDemoDataContext } from './DemoPortal';
import { PortalContainer } from '../portal/PortalContainer';

// Portfolio property data for demo
interface PortfolioProperty {
  image: string;
  title: string;
  location: string;
  shares: number;
  invested: string;
  currentValue: string;
  returns: string;
  returnsPercent: number;
  type: string;
  data: Array<{ month: string; value: number }>;
}

interface DashboardTransaction {
  id: string;
  amount: number;
  status: string;
  initiatedAt: string;
}

export function PortalDashboardContent() {
  const navigate = useNavigate();
  const handleNavigateToInvest = () => navigate('/portal/invest/properties');
  const demoData = useDemoDataContext();

  const [isLoading, setIsLoading] = useState(true);
  const [portfolioValue, setPortfolioValue] = useState(0);
  const [totalReturn, setTotalReturn] = useState(0);
  const [returnPercentage, setReturnPercentage] = useState(0);
  const [portfolioPositions, setPortfolioPositions] = useState<PortfolioPosition[]>([]);
  const [annualReturn, setAnnualReturn] = useState(0);
  const [transactions, setTransactions] = useState<DashboardTransaction[]>([]);
  const [transactionsLoading, setTransactionsLoading] = useState(true);

  // Demo portfolio properties with chart data
  const portfolioProperties: PortfolioProperty[] = demoData ? [
    {
      image: 'https://images.unsplash.com/photo-1619647787040-5583f41eb9b5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
      title: 'Skyline Tower Residences',
      location: 'Manhattan, New York',
      shares: 15,
      invested: '$2,250',
      currentValue: '$2,531',
      returns: '+$281',
      returnsPercent: 12.5,
      type: 'Residential',
      data: [
        { month: 'Jan', value: 2250 },
        { month: 'Feb', value: 2363 },
        { month: 'Mar', value: 2318 },
        { month: 'Apr', value: 2430 },
        { month: 'May', value: 2520 },
        { month: 'Jun', value: 2531 },
      ],
    },
    {
      image: 'https://images.unsplash.com/photo-1632398793634-e3cd63fc9e84?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
      title: 'Downtown Commerce Hub',
      location: 'San Francisco, California',
      shares: 8,
      invested: '$1,600',
      currentValue: '$1,845',
      returns: '+$245',
      returnsPercent: 15.3,
      type: 'Commercial',
      data: [
        { month: 'Jan', value: 1600 },
        { month: 'Feb', value: 1712 },
        { month: 'Mar', value: 1744 },
        { month: 'Apr', value: 1792 },
        { month: 'May', value: 1824 },
        { month: 'Jun', value: 1845 },
      ],
    },
    {
      image: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&w=1080',
      title: 'Lakeside Luxury Apartments',
      location: 'Chicago, Illinois',
      shares: 12,
      invested: '$1,500',
      currentValue: '$1,662',
      returns: '+$162',
      returnsPercent: 10.8,
      type: 'Residential',
      data: [
        { month: 'Jan', value: 1500 },
        { month: 'Feb', value: 1530 },
        { month: 'Mar', value: 1560 },
        { month: 'Apr', value: 1590 },
        { month: 'May', value: 1620 },
        { month: 'Jun', value: 1662 },
      ],
    },
  ] : [];

  // Portfolio performance data for charts
  const portfolioData = demoData ? [
    { month: 'Jan', value: 5350 },
    { month: 'Feb', value: 5605 },
    { month: 'Mar', value: 5622 },
    { month: 'Apr', value: 5812 },
    { month: 'May', value: 5964 },
    { month: 'Jun', value: 6038 },
  ] : (() => {
    const now = new Date();
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      months.push(d.toLocaleString('en-US', { month: 'short' }));
    }
    const val = portfolioValue || 0;
    return months.map(month => ({ month, value: val }));
  })();

  const ALLOCATION_COLORS = ['#F9EF23', '#1944FF', '#F0F0F0', '#A855F7', '#10B981', '#F97316'];
  const allocationData = demoData ? [
    { name: 'Residential', value: 53, color: '#F9EF23' },
    { name: 'Commercial', value: 27, color: '#1944FF' },
    { name: 'Mixed-Use', value: 20, color: '#F0F0F0' },
  ] : (() => {
    if (!portfolioPositions.length) return [];
    const totalValue = portfolioPositions.reduce((s, p) => s + (p.current_value || 0), 0);
    if (totalValue === 0) return [];
    const grouped: Record<string, number> = {};
    for (const p of portfolioPositions) {
      const key = p.property_name || 'Other';
      grouped[key] = (grouped[key] || 0) + (p.current_value || 0);
    }
    return Object.entries(grouped).map(([name, val], i) => ({
      name,
      value: Math.round((val / totalValue) * 100),
      color: ALLOCATION_COLORS[i % ALLOCATION_COLORS.length],
    }));
  })();

  const loadPortfolioData = useCallback(async () => {
    try {
      setIsLoading(true);

      if (demoData) {
        setPortfolioValue(demoData.dashboardSummary.totalPortfolioValue);
        setTotalReturn(demoData.dashboardSummary.totalReturn);
        setReturnPercentage(demoData.dashboardSummary.totalReturnPercentage);
        setAnnualReturn(demoData.dashboardSummary.totalReturnPercentage || 12.9);

        const positions: PortfolioPosition[] = demoData.portfolioHoldings.map(holding => ({
          property_id: holding.propertyId,
          property_name: holding.propertyName,
          shares_owned: holding.sharesOwned,
          shares_staked: 0,
          investment_amount: holding.initialInvestment,
          current_value: holding.currentValue,
          return_amount: holding.currentValue - holding.initialInvestment,
          return_percentage: holding.returnPercentage,
          dividends_earned: holding.lastDividend,
        }));
        setPortfolioPositions(positions);
        setIsLoading(false);
        return;
      }

      const [summaryRes, positionsRes] = await Promise.all([
        portfolioApi.getSummary().catch(() => ({ success: false, data: null })),
        portfolioApi.getPositions().catch(() => ({ success: false, data: null }))
      ]);

      if (summaryRes.success && summaryRes.data) {
        setPortfolioValue(summaryRes.data.total_value || 0);
        setTotalReturn(summaryRes.data.total_return || 0);
        setReturnPercentage(summaryRes.data.return_percentage || 0);
        setAnnualReturn(summaryRes.data.annualReturn || 0);
      }

      if (positionsRes.success && positionsRes.data) {
        setPortfolioPositions(positionsRes.data || []);
      }
    } catch (error) {
      console.error('Failed to load portfolio data:', error);
    } finally {
      setIsLoading(false);
    }
  }, [demoData]);

  const loadTransactions = useCallback(async () => {
    try {
      setTransactionsLoading(true);

      if (demoData) {
        setTransactions([
          {
            id: 'txn_demo_01',
            amount: 5000000,
            status: 'processing',
            initiatedAt: new Date().toISOString(),
          },
          {
            id: 'txn_demo_02',
            amount: 1250000,
            status: 'settled',
            initiatedAt: new Date(Date.now() - 86400000 * 6).toISOString(),
          },
          {
            id: 'txn_demo_03',
            amount: 750000,
            status: 'returned',
            initiatedAt: new Date(Date.now() - 86400000 * 14).toISOString(),
          },
        ]);
        return;
      }

      const res = await getMyTransactions({ limit: 6 });
      const rows = (res.data || []).map((tx: any) => ({
        id: tx.txn_id || tx.id,
        amount: tx.amount_cents || 0,
        status: tx.status,
        initiatedAt: tx.created_at || new Date().toISOString(),
      }));

      setTransactions(rows);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setTransactionsLoading(false);
    }
  }, [demoData]);

  useEffect(() => {
    loadPortfolioData();
  }, [loadPortfolioData]);

  useEffect(() => {
    loadTransactions();
  }, [loadTransactions]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatPercentage = (percentage: number) => {
    const sign = percentage >= 0 ? '+' : '';
    return `${sign}${percentage.toFixed(1)}%`;
  };

  const formatDate = (value: string) => new Date(value).toLocaleDateString();

  const getTransactionStatus = (status: string) => {
    const normalized = status?.toLowerCase?.() || 'pending';

    switch (normalized) {
      case 'settled':
      case 'succeeded':
        return { label: 'Funded', className: 'bg-[hsl(var(--success))]/10 text-[hsl(var(--success))]' };
      case 'returned':
      case 'refunded':
        return { label: 'Refunded', className: 'bg-destructive/10 text-destructive' };
      case 'failed':
        return { label: 'Failed', className: 'bg-destructive/10 text-destructive' };
      case 'processing':
        return { label: 'ACH pending', className: 'bg-primary/10 text-primary' };
      default:
        return { label: 'Pending', className: 'bg-muted text-muted-foreground' };
    }
  };

  // Calculate totals from demo properties
  const totalInvested = portfolioProperties.reduce(
    (sum, prop) => sum + parseFloat(prop.invested.replace(/[$,]/g, '')),
    0
  );
  const totalValue = portfolioProperties.reduce(
    (sum, prop) => sum + parseFloat(prop.currentValue.replace(/[$,]/g, '')),
    0
  );
  const totalReturns = totalValue - totalInvested;
  const totalReturnsPercent = totalInvested > 0 ? ((totalReturns / totalInvested) * 100).toFixed(1) : '0.0';

  if (isLoading) {
    return (
      <PortalContainer>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </PortalContainer>
    );
  }

  return (
    <PortalContainer>
      {/* Portfolio Value Banner */}
      <Card className="p-6 lg:p-8 border-border bg-[#F0F0F0] mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <div className="flex-1">
            <p className="text-sm text-muted-foreground mb-2">Total Portfolio Value</p>
            <div className="flex items-baseline gap-3">
              <h2 className="text-4xl lg:text-5xl font-display font-semibold text-[#242424]">
                ${(demoData ? totalValue : portfolioValue).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </h2>
              <div className="flex items-center gap-1.5 text-lg text-[hsl(var(--success))]">
                <TrendingUp className="w-5 h-5" />
                <span className="font-semibold">+{demoData ? totalReturnsPercent : returnPercentage.toFixed(1)}%</span>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-2">
              +${(demoData ? totalReturns : totalReturn).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} all time
            </p>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            <div>
              <p className="text-xs text-muted-foreground mb-1">Total Invested</p>
              <p className="text-xl font-semibold text-[#242424]">
                ${(demoData ? totalInvested : portfolioValue - totalReturn).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1">Investment Positions</p>
              <p className="text-xl font-semibold text-[#242424]">{demoData ? portfolioProperties.length : portfolioPositions.length}</p>
            </div>
            <div className="col-span-2 lg:col-span-1">
              <p className="text-xs text-muted-foreground mb-1">Annual Return</p>
              <p className="text-xl font-semibold text-[#242424]">{annualReturn > 0 ? `${annualReturn}%` : '—'}</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Performance Chart */}
      <Card className="p-6 border-border mb-6">
        <div className="mb-6">
          <h3 className="text-lg font-display font-semibold text-[#242424] mb-1">Performance</h3>
          <p className="text-sm text-muted-foreground">Last 6 months</p>
        </div>

        {portfolioData.every(d => d.value === 0) ? (
          <div className="h-64 md:h-72 flex items-center justify-center">
            <div className="text-center max-w-md mx-auto">
              <AlertCircle className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground mb-4">
                Your portfolio performance chart will appear here once you make your first investment
              </p>
              <Button onClick={handleNavigateToInvest} className="bg-primary text-primary-foreground hover:bg-primary/90">
                Browse Investment Opportunities
              </Button>
            </div>
          </div>
        ) : (
          <div className="h-64 md:h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={portfolioData}>
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#242424" stopOpacity={0.15}/>
                    <stop offset="95%" stopColor="#242424" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <XAxis
                  dataKey="month"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={12}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(value) => `$${(value / 1000).toFixed(1)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'hsl(var(--card))',
                    border: '1px solid hsl(var(--border))',
                    borderRadius: '8px',
                    color: 'hsl(var(--foreground))',
                  }}
                  formatter={(value: number) => [`$${value.toLocaleString()}`, 'Value']}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#242424"
                  strokeWidth={2}
                  fillOpacity={1}
                  fill="url(#colorValue)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Your Investments */}
      {demoData && portfolioProperties.length > 0 && (
        <div className="mb-6">
          <div className="mb-4">
            <h3 className="text-lg font-display font-semibold text-[#242424]">Your Investments</h3>
            <p className="text-sm text-muted-foreground">{portfolioProperties.length} properties</p>
          </div>

          <div className="space-y-4">
            {portfolioProperties.map((property, index) => (
              <PortfolioCard key={index} {...property} />
            ))}
          </div>
        </div>
      )}

      {/* Asset Allocation & Investments (non-demo) */}
      {!demoData && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <Card className="p-4 md:p-6 border-border">
            <h3 className="font-display font-semibold text-[#242424] mb-4">Asset Allocation</h3>
            {allocationData.length === 0 ? (
              <div className="h-48 flex items-center justify-center">
                <div className="text-center px-4">
                  <PieChart className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground mb-3">
                    Your asset allocation will appear here
                  </p>
                </div>
              </div>
            ) : (
              <>
                <div className="h-48 flex items-center justify-center mb-4">
                  <ResponsiveContainer width="100%" height="100%">
                    <RePieChart>
                      <Pie
                        data={allocationData}
                        cx="50%"
                        cy="50%"
                        innerRadius={50}
                        outerRadius={70}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {allocationData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'hsl(var(--card))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                          color: 'hsl(var(--foreground))',
                        }}
                        formatter={(value: number) => [`${value}%`, 'Allocation']}
                      />
                    </RePieChart>
                  </ResponsiveContainer>
                </div>
                <div className="space-y-2">
                  {allocationData.map((item) => (
                    <div key={item.name} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item.color }} />
                        <span className="text-[#242424]">{item.name}</span>
                      </div>
                      <span className="font-semibold text-[#242424]">{item.value}%</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </Card>

          <div className="lg:col-span-2">
            <h3 className="font-display font-semibold text-[#242424] mb-4">Your Investments</h3>
            {portfolioPositions.length === 0 ? (
              <Card className="p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-[#F0F0F0] mx-auto mb-4 flex items-center justify-center border border-border">
                  <Home className="w-8 h-8 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground mb-4">No investments yet</p>
                <Button onClick={handleNavigateToInvest} className="bg-primary text-primary-foreground hover:bg-primary/90">
                  Start Investing
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {portfolioPositions.map((position) => (
                  <Card key={position.property_id} className="p-4 border-border hover:border-[hsl(var(--chart-2))]/30 transition-colors">
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-[#242424]">{position.property_name}</h4>
                        <p className="text-sm text-muted-foreground">
                          {position.shares_owned > 0 ? `${position.shares_owned} shares` : 'Fund Investment'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold text-[#242424]">{formatCurrency(position.current_value)}</p>
                        <p className={`text-sm ${position.return_amount >= 0 ? 'text-[hsl(var(--success))]' : 'text-destructive'}`}>
                          {formatPercentage(position.return_percentage)}
                        </p>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      <Card className="p-6 border-border mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-lg font-display font-semibold text-[#242424]">Recent Transactions</h3>
            <p className="text-sm text-muted-foreground">Pending, processing, funded, failed, refunded</p>
          </div>
            <Button variant="ghost" onClick={() => navigate('/portal/payments')}>View All</Button>
        </div>

        {transactionsLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="animate-spin text-primary" size={28} />
          </div>
        ) : transactions.length === 0 ? (
          <div className="text-center py-10">
            <p className="text-sm text-muted-foreground">No transactions yet.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {transactions.slice(0, 6).map((tx) => {
              const status = getTransactionStatus(tx.status);
              return (
                <div
                  key={tx.id}
                  className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 rounded-lg border border-border bg-card px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground">Transaction ID</p>
                    <p className="text-sm font-mono text-foreground break-all">{tx.id}</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div>
                      <p className="text-xs text-muted-foreground">Date</p>
                      <p className="text-sm font-medium text-foreground">{formatDate(tx.initiatedAt)}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Amount</p>
                      <p className="text-sm font-semibold text-foreground">{formatCurrency(tx.amount / 100)}</p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${status.className}`}>
                      {status.label}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Explore More */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-display font-semibold text-[#242424]">Explore More Opportunities</h3>
            <p className="text-sm text-muted-foreground">Discover new investments</p>
          </div>
          <Button variant="ghost" onClick={handleNavigateToInvest}>View All</Button>
        </div>

        <Card className="p-8 text-center border-dashed">
          <p className="text-muted-foreground mb-4">
            Browse available properties and funds to grow your portfolio
          </p>
          <Button onClick={handleNavigateToInvest} className="bg-primary text-primary-foreground hover:bg-primary/90">
            Browse Investment Opportunities
          </Button>
        </Card>
      </div>
    </PortalContainer>
  );
}
